import { useEffect, useState } from "react";
import { Dialog, TextField } from "@mui/material";
import MapComponent from "../../../frontend/components/MapNew";
import styles from "./manuallocationdialog.module.css";

const DEFAULT_CENTER = { lat: 51.509865, lng: -0.118092 };

// A popup for addresses Google Places can't find. The user drags a pin to
// the right spot on the map; we reverse-geocode that point into an address
// and post code as a starting suggestion, and they can freely edit either
// before confirming. Confirming is what writes lat/lng + address + post
// code back into the parent form — nothing here touches the parent state
// until "Use this location" is pressed.
function ManualLocationDialog({ open, onClose, onConfirm, initialCenter, showPostCode = true }) {
    const [center, setCenter] = useState(initialCenter || DEFAULT_CENTER);
    const [hasPin, setHasPin] = useState(!!initialCenter);
    const [address, setAddress] = useState("");
    const [postCode, setPostCode] = useState("");
    const [geocoding, setGeocoding] = useState(false);
    const [geocodeNote, setGeocodeNote] = useState("");

    useEffect(() => {
        if (open) {
            setCenter(initialCenter || DEFAULT_CENTER);
            setHasPin(!!initialCenter);
            setAddress("");
            setPostCode("");
            setGeocodeNote("");
        }
    }, [open, initialCenter]);

    // Reverse-geocodes via the Google Maps JS SDK (already loaded for Places
    // Autocomplete elsewhere in the app, so no extra key/request is needed).
    // This is just a convenience prefill — if it comes back empty or wrong,
    // the user types the address themselves.
    const reverseGeocode = (lat, lng) => {
        if (!window.google?.maps) {
            setGeocodeNote("Type the address manually below.");
            return;
        }
        setGeocoding(true);
        setGeocodeNote("");
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            setGeocoding(false);
            if (status === "OK" && results?.[0]) {
                setAddress(results[0].formatted_address || "");
                const postal = results[0].address_components?.find((c) => c.types.includes("postal_code"));
                setPostCode(postal ? postal.long_name : "");
                setGeocodeNote("Suggested from the pin — check and edit if needed.");
            } else {
                setGeocodeNote("Couldn't suggest an address for this spot — type it in below.");
            }
        });
    };

    // Fires on either a map click or a marker drag, both of which move the pin.
    const handlePinMove = (lat, lng) => {
        setCenter({ lat, lng });
        setHasPin(true);
        reverseGeocode(lat, lng);
    };

    const handleConfirm = () => {
        onConfirm({
            address,
            ...(showPostCode ? { post_code: postCode } : {}),
            latitude: center.lat,
            longitude: center.lng,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <div className={styles.BulkAddModal}>
                <h3 className={styles.BulkAddTitle}>Set the location manually</h3>
                <p className={styles.BulkAddSub}>
                    Drag the pin to the exact spot on the map. We'll suggest {showPostCode ? "an address and post code" : "an address"} below, which you can edit.
                </p>

                <div style={{ marginBottom: 16 }}>
                    <MapComponent
                        center={center}
                        zoom={hasPin ? 17 : 13}
                        isMarker={hasPin}
                        draggableMarker
                        onMapClick={handlePinMove}
                        onMarkerDragEnd={handlePinMove}
                    />
                </div>

                {!hasPin && (
                    <p className={styles.ErrorM} style={{ color: "var(--ink-500)", marginBottom: 12 }}>
                        Tap the map to drop a pin first.
                    </p>
                )}

                <div className={styles.BulkAddField}>
                    <TextField
                        fullWidth
                        label="Address"
                        variant="outlined"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={!hasPin}
                        placeholder="Full address"
                    />
                </div>
                {showPostCode && (
                    <div className={styles.BulkAddField} style={{ marginTop: 14 }}>
                        <TextField
                            fullWidth
                            label="Post Code"
                            variant="outlined"
                            value={postCode}
                            onChange={(e) => setPostCode(e.target.value)}
                            disabled={!hasPin}
                        />
                    </div>
                )}

                {geocodeNote && (
                    <p className={styles.AltPhonesLabel} style={{ marginTop: 10 }}>
                        {geocoding ? "Looking up the address…" : geocodeNote}
                    </p>
                )}

                <div className={styles.BulkAddActions}>
                    <button type="button" className={styles.BulkAddCancelBU} onClick={onClose}>Cancel</button>
                    <button
                        type="button"
                        className={styles.BulkAddConfirmBU}
                        onClick={handleConfirm}
                        disabled={!hasPin || !address.trim()}
                    >
                        Use this location
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

export default ManualLocationDialog;